import { useEffect, useRef, useState } from 'react';
import {
  PlaneTakeoff, Fuel, CalendarDays, Radar, MessageSquare,
  CalendarCheck, Truck, Receipt, ClipboardList, MapPin,
  RefreshCw, GraduationCap, ShieldAlert, TrendingUp,
  Wrench, DollarSign, BarChart3, Warehouse,
} from 'lucide-react';

const W = 680, H = 680, CX = W / 2, CY = H / 2;

// Map label → Lucide component + emoji fallback for canvas
const ICON_MAP = {
  'Movement & Billing':   { Ic: PlaneTakeoff,  emoji: '✈' },
  'Fuel Management':      { Ic: Fuel,           emoji: '⛽' },
  'Staff Scheduling':     { Ic: CalendarDays,   emoji: '📅' },
  'Live Traffic Map':     { Ic: Radar,          emoji: '🗺' },
  'Client Chat':          { Ic: MessageSquare,  emoji: '💬' },
  'Appointments':         { Ic: CalendarCheck,  emoji: '🔧' },
  'Fleet Management':     { Ic: Truck,          emoji: '🚗' },
  'Invoicing & Hangars':  { Ic: Receipt,        emoji: '🧾' },
  'Inspections':          { Ic: ClipboardList,  emoji: '📋' },
  'Airport Zones':        { Ic: MapPin,         emoji: '📍' },
  'Recurring Tasks':      { Ic: RefreshCw,      emoji: '🔁' },
  'Certifications':       { Ic: GraduationCap,  emoji: '🎓' },
  'Deletion Audit':       { Ic: ShieldAlert,    emoji: '🗑' },
  'Fuel Analytics':       { Ic: TrendingUp,     emoji: '📈' },
  'Maint Analytics':      { Ic: Wrench,         emoji: '🔧' },
  'Financial Analytics':  { Ic: DollarSign,     emoji: '💵' },
  'Flight Analytics':     { Ic: BarChart3,      emoji: '✈' },
  'Hangar Analytics':     { Ic: Warehouse,      emoji: '🏗' },
};

const MODULES = [
  { label: 'Movement & Billing',   group: 0, color: '#3b82f6' },
  { label: 'Fuel Management',      group: 0, color: '#f97316' },
  { label: 'Staff Scheduling',     group: 0, color: '#eab308' },
  { label: 'Live Traffic Map',     group: 0, color: '#06b6d4' },
  { label: 'Client Chat',          group: 0, color: '#ec4899' },
  { label: 'Appointments',         group: 0, color: '#22c55e' },
  { label: 'Fleet Management',     group: 0, color: '#a855f7' },
  { label: 'Invoicing & Hangars',  group: 0, color: '#ef4444' },
  { label: 'Inspections',          group: 1, color: '#3b82f6' },
  { label: 'Airport Zones',        group: 1, color: '#06b6d4' },
  { label: 'Recurring Tasks',      group: 1, color: '#eab308' },
  { label: 'Certifications',       group: 1, color: '#22c55e' },
  { label: 'Deletion Audit',       group: 1, color: '#ef4444' },
  { label: 'Fuel Analytics',       group: 2, color: '#3b82f6' },
  { label: 'Maint Analytics',      group: 2, color: '#f97316' },
  { label: 'Financial Analytics',  group: 2, color: '#22c55e' },
  { label: 'Flight Analytics',     group: 2, color: '#06b6d4' },
  { label: 'Hangar Analytics',     group: 2, color: '#a855f7' },
];

const MODULE_DETAILS = {
  'Movement & Billing':   { tag: 'Automated',   desc: 'Auto-detect takeoffs and landings. Every movement captured and billed instantly.' },
  'Fuel Management':      { tag: 'Real-time',   desc: 'Track stock, process orders, manage replenishment and fuel invoices end-to-end.' },
  'Staff Scheduling':     { tag: 'Planning',    desc: 'Drag-and-drop planning, shift management, and workload tracking for ground ops.' },
  'Live Traffic Map':     { tag: 'Live',        desc: 'Real-time radar-style view — track callsigns, speed, altitude live.' },
  'Client Chat':          { tag: 'Comms',       desc: 'Built-in messaging, all communications logged and searchable in one place.' },
  'Appointments':         { tag: 'Self-service',desc: 'Client self-service booking for fuel, maintenance, and pre-flight checks.' },
  'Fleet Management':     { tag: 'Fleet',       desc: 'Track ground vehicles — status, availability, history, and usage logs.' },
  'Invoicing & Hangars':  { tag: 'Billing',     desc: 'Full billing, export-ready reports, and lease management for hangars.' },
  'Inspections':          { tag: 'Compliance',  desc: 'Schedule runway, apron, and facility inspections with digital checklists.' },
  'Airport Zones':        { tag: 'Zones',       desc: 'Define and manage restricted, operational, and safety zones.' },
  'Recurring Tasks':      { tag: 'Automation',  desc: 'Automate compliance tasks with configurable recurrence schedules.' },
  'Certifications':       { tag: 'Compliance',  desc: 'Track staff and equipment certs, expiry alerts, and compliance records.' },
  'Deletion Audit':       { tag: 'Audit',       desc: 'Full audit trail of deleted records across all modules.' },
  'Fuel Analytics':       { tag: 'Analytics',   desc: 'Visualize consumption trends, top clients by volume, and stock rotation.' },
  'Maint Analytics':      { tag: 'Analytics',   desc: 'Track maintenance frequencies, recurring issues, and technician workload.' },
  'Financial Analytics':  { tag: 'Analytics',   desc: 'Revenue by service type, billing summaries, and profit margin tracking.' },
  'Flight Analytics':     { tag: 'Analytics',   desc: 'Movement volume over time, peak traffic hours, and aircraft type breakdown.' },
  'Hangar Analytics':     { tag: 'Analytics',   desc: 'Occupancy rates, lease renewal timelines, and hangar utilization metrics.' },
};

const GROUPS = [
  { name: 'Core Modules',       color: '#3b82f6' },
  { name: 'Airport Operations', color: '#06b6d4' },
  { name: 'Analytics',          color: '#a855f7' },
];

const LEFT_MODULES  = MODULES.slice(0, 9);
const RIGHT_MODULES = MODULES.slice(9);

function hexToRgba(hex, a) {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
}

function buildNodes() {
  const g0 = MODULES.filter(m => m.group === 0);
  const g1 = MODULES.filter(m => m.group === 1);
  const g2 = MODULES.filter(m => m.group === 2);
  return MODULES.map(m => {
    let angle, radius;
    if (m.group === 0) {
      const i = g0.indexOf(m);
      angle = (i / g0.length) * Math.PI * 2 - Math.PI / 2; radius = 175;
    } else if (m.group === 1) {
      const i = g1.indexOf(m);
      angle = (-Math.PI/2 - 0.3) + (i/(g1.length-1)) * (Math.PI*0.85); radius = 295;
    } else {
      const i = g2.indexOf(m);
      angle = (Math.PI/2 - 0.25) + (i/(g2.length-1)) * (Math.PI*0.75); radius = 295;
    }
    return { ...m,
      emoji: ICON_MAP[m.label]?.emoji ?? '●',
      angle, radius,
      ox: CX + Math.cos(angle)*radius, oy: CY + Math.sin(angle)*radius,
      x:  CX + Math.cos(angle)*radius, y:  CY + Math.sin(angle)*radius,
      r: 28, pulse: Math.random()*Math.PI*2,
    };
  });
}

function buildConnections(nodes) {
  const c = [];
  for (let i=0;i<nodes.length;i++) for (let j=i+1;j<nodes.length;j++) {
    const dx=nodes[i].ox-nodes[j].ox, dy=nodes[i].oy-nodes[j].oy;
    if (Math.sqrt(dx*dx+dy*dy)<200) c.push([i,j]);
  }
  return c;
}

// ─── Icon bubble (SVG Lucide) ─────────────────────────────────────────────────
function IconBubble({ module, size=36, fontSize=18 }) {
  const { Ic } = ICON_MAP[module.label] || {};
  const isHov = false; // static version used in cards
  return (
    <div style={{
      width: size, height: size, borderRadius:'50%', flexShrink:0,
      background: hexToRgba(module.color, 0.15),
      display:'flex', alignItems:'center', justifyContent:'center',
    }}>
      {Ic ? <Ic size={fontSize} color={module.color} strokeWidth={1.8} /> : null}
    </div>
  );
}

// ─── Mini Card ────────────────────────────────────────────────────────────────
function MiniCard({ module, hoveredModule, nodesRef, hoveredRef, setHoveredModule }) {
  const d = MODULE_DETAILS[module.label];
  const isHov = hoveredModule?.label === module.label;
  const { Ic } = ICON_MAP[module.label] || {};

  return (
    <div
      onMouseEnter={() => {
        const n = nodesRef.current?.find(n => n.label === module.label);
        if (n) { hoveredRef.current = n; setHoveredModule(n); }
      }}
      onMouseLeave={() => { hoveredRef.current = null; setHoveredModule(null); }}
      style={{
        flex: '1 1 0', minWidth: 0,
        display: 'flex', flexDirection: 'column', gap: 7,
        padding: '12px 14px',
        background: isHov ? hexToRgba(module.color, 0.1) : 'rgba(255,255,255,0.03)',
        borderRadius: 12,
        border: `1px solid ${isHov ? hexToRgba(module.color, 0.4) : 'rgba(255,255,255,0.06)'}`,
        cursor: 'pointer', transition: 'all 0.2s',
        boxShadow: isHov ? `0 0 18px ${hexToRgba(module.color, 0.18)}` : 'none',
      }}
    >
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        {/* SVG icon bubble */}
        <div style={{
          width:38, height:38, borderRadius:'50%', flexShrink:0,
          background: hexToRgba(module.color, isHov ? 0.22 : 0.13),
          border: `1px solid ${hexToRgba(module.color, isHov ? 0.5 : 0.2)}`,
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow: isHov ? `0 0 12px ${hexToRgba(module.color, 0.35)}` : 'none',
          transition:'all 0.2s',
        }}>
          {Ic && <Ic size={18} color={module.color} strokeWidth={1.8} />}
        </div>
        <div style={{ minWidth:0, flex:1 }}>
          <div style={{
            fontFamily:"'DM Sans',system-ui", fontSize:13, fontWeight:700,
            color: isHov ? '#ffffff' : '#f1f5f9',
            whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
            transition:'color 0.2s',
          }}>
            {module.label}
          </div>
          <span style={{
            background: hexToRgba(module.color, 0.2), color: module.color,
            fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:20,
            display:'inline-block', letterSpacing:'0.5px', marginTop:2,
          }}>{d?.tag}</span>
        </div>
      </div>
      <div style={{ fontSize:11, lineHeight:1.5, color: isHov ? '#94a3b8' : '#4e5f74' }}>
        {d?.desc}
      </div>
    </div>
  );
}

// ─── Card Grid ────────────────────────────────────────────────────────────────
function CardGrid({ modules, sharedProps }) {
  const rows = [];
  for (let i=0; i<modules.length; i+=3) rows.push(modules.slice(i,i+3));
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {rows.map((row,ri) => (
        <div key={ri} style={{ display:'flex', gap:8 }}>
          {row.map(m => <MiniCard key={m.label} module={m} {...sharedProps} />)}
          {row.length < 3 && Array(3-row.length).fill(null).map((_,i) => (
            <div key={`e${i}`} style={{ flex:'1 1 0' }} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Section Label ────────────────────────────────────────────────────────────
function SectionLabel({ label, color, align='left' }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
      {align==='right' && <div style={{ flex:1, height:1, background:hexToRgba(color,0.2) }}/>}
      <div style={{ width:7, height:7, borderRadius:'50%', background:color, boxShadow:`0 0 7px ${color}` }}/>
      <span style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color, letterSpacing:'1.5px', textTransform:'uppercase', fontWeight:600 }}>{label}</span>
      {align==='left'  && <div style={{ flex:1, height:1, background:hexToRgba(color,0.2) }}/>}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function ModuleWeb() {
  const canvasRef    = useRef(null);
  const wrapRef      = useRef(null);
  const rafRef       = useRef(null);
  const runningRef   = useRef(false);
  const hoveredRef   = useRef(null);
  const frameRef     = useRef(0);
  const ptTimerRef   = useRef(0);
  const particlesRef = useRef([]);
  const nodesRef     = useRef(null);
  const connsRef     = useRef(null);
  const scaleRef     = useRef(1);

  const [hoveredModule, setHoveredModule] = useState(null);
  const [tooltipPos,    setTooltipPos]    = useState({ x:0, y:0 });
  const [canvasSize,    setCanvasSize]    = useState(680);

  // Responsive canvas sizing
  useEffect(() => {
    function measure() {
      if (!wrapRef.current) return;
      const total = wrapRef.current.offsetWidth;
      const size  = Math.max(320, Math.min(680, total - 2 * 320 - 36));
      setCanvasSize(size);
      scaleRef.current = size / 680;
    }
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // Canvas draw loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    nodesRef.current = buildNodes();
    connsRef.current = buildConnections(nodesRef.current);
    particlesRef.current = []; frameRef.current = 0; ptTimerRef.current = 0;
    runningRef.current = true;

    function spawnParticle(from, to) {
      particlesRef.current.push({ from, to, t:0, speed:0.003+Math.random()*0.004 });
    }
    function glowLine(x1,y1,x2,y2,color,alpha) {
      ctx.beginPath(); ctx.moveTo(x1,y1); ctx.lineTo(x2,y2);
      ctx.strokeStyle=hexToRgba(color,alpha); ctx.lineWidth=0.8; ctx.stroke();
    }
    function drawNode(n) {
      const isHov=hoveredRef.current===n, f=frameRef.current;
      const pulseMul=Math.sin(n.pulse+f*0.04)*0.5+0.5;
      const r=n.r*(isHov?1.15:(Math.sin(n.pulse+f*0.04)*0.15+0.85)*0.98);
      if (isHov) {
        const gr=ctx.createRadialGradient(n.x,n.y,r,n.x,n.y,r+18);
        gr.addColorStop(0,hexToRgba(n.color,0.25)); gr.addColorStop(1,hexToRgba(n.color,0));
        ctx.beginPath(); ctx.arc(n.x,n.y,r+18,0,Math.PI*2); ctx.fillStyle=gr; ctx.fill();
      }
      ctx.beginPath(); ctx.arc(n.x,n.y,r+6+pulseMul*8,0,Math.PI*2);
      ctx.strokeStyle=hexToRgba(n.color,0.08+pulseMul*0.1); ctx.lineWidth=1; ctx.stroke();
      ctx.beginPath(); ctx.arc(n.x,n.y,r,0,Math.PI*2); ctx.fillStyle='#0f172a'; ctx.fill();
      ctx.beginPath(); ctx.arc(n.x,n.y,r,0,Math.PI*2);
      ctx.strokeStyle=hexToRgba(n.color,isHov?0.9:0.4); ctx.lineWidth=isHov?1.5:1; ctx.stroke();

      // Draw Lucide icon via SVG path on canvas using Path2D
      const iconSize = r * 1.0;
      const ix = n.x - iconSize/2, iy = n.y - iconSize/2;
      ctx.save();
      ctx.translate(ix, iy);
      ctx.scale(iconSize/24, iconSize/24);
      ctx.strokeStyle = isHov ? '#ffffff' : hexToRgba(n.color, 0.9);
      ctx.lineWidth = 1.8 * (24/iconSize);
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.fillStyle = 'none';

      const paths = CANVAS_PATHS[n.label];
      if (paths) {
        paths.forEach(d => {
          const p = new Path2D(d);
          ctx.stroke(p);
        });
      } else {
        // fallback text
        ctx.restore();
        ctx.font=`${r*0.6}px serif`; ctx.textAlign='center'; ctx.textBaseline='middle';
        ctx.fillStyle='white'; ctx.fillText(n.emoji,n.x,n.y);
        return;
      }
      ctx.restore();
    }
    function drawCenter() {
      const r=44, t=frameRef.current*0.015;
      for (let i=0;i<3;i++) {
        ctx.beginPath(); ctx.arc(CX,CY,r+14+i*10,0,Math.PI*2);
        ctx.strokeStyle=`rgba(59,130,246,${0.06-i*0.015})`; ctx.lineWidth=1; ctx.stroke();
      }
      ctx.save(); ctx.translate(CX,CY); ctx.rotate(t);
      ctx.beginPath(); ctx.arc(0,0,r+10,0,Math.PI*2);
      ctx.strokeStyle='rgba(59,130,246,0.2)'; ctx.lineWidth=1.5;
      ctx.setLineDash([4,8]); ctx.stroke(); ctx.setLineDash([]); ctx.restore();
      ctx.save(); ctx.translate(CX,CY); ctx.rotate(-t*0.7);
      ctx.beginPath(); ctx.arc(0,0,r+18,0,Math.PI*2);
      ctx.strokeStyle='rgba(59,130,246,0.1)'; ctx.lineWidth=1;
      ctx.setLineDash([2,12]); ctx.stroke(); ctx.setLineDash([]); ctx.restore();
      ctx.beginPath(); ctx.arc(CX,CY,r,0,Math.PI*2); ctx.fillStyle='#0a1628'; ctx.fill();
      ctx.strokeStyle='rgba(59,130,246,0.7)'; ctx.lineWidth=1.5; ctx.stroke();
    }
    function loop() {
      if (!runningRef.current) return;
      const nodes=nodesRef.current, conns=connsRef.current, particles=particlesRef.current;
      const frame=++frameRef.current, hov=hoveredRef.current;
      ptTimerRef.current++;
      if (ptTimerRef.current%18===0) spawnParticle(Math.floor(Math.random()*nodes.length),-1);
      if (ptTimerRef.current%30===0) { const c=conns[Math.floor(Math.random()*conns.length)]; if(c) spawnParticle(c[0],c[1]); }
      ctx.clearRect(0,0,W,H);
      nodes.forEach(n => { n.x=n.ox+Math.sin(frame*0.008+n.pulse)*5; n.y=n.oy+Math.cos(frame*0.010+n.pulse)*4; });
      nodes.forEach(n => glowLine(CX,CY,n.x,n.y,n.color,hov===n?0.25:0.07));
      conns.forEach(([i,j]) => { const ni=nodes[i],nj=nodes[j]; glowLine(ni.x,ni.y,nj.x,nj.y,ni.color,(hov===ni||hov===nj)?0.3:0.05); });
      for (let i=particles.length-1;i>=0;i--) {
        const p=particles[i]; p.t+=p.speed;
        if(p.t>=1){particles.splice(i,1);continue;}
        const x1=nodes[p.from].x,y1=nodes[p.from].y;
        const x2=p.to===-1?CX:nodes[p.to].x, y2=p.to===-1?CY:nodes[p.to].y;
        const px=x1+(x2-x1)*p.t, py=y1+(y2-y1)*p.t;
        const alpha=Math.sin(p.t*Math.PI);
        ctx.beginPath(); ctx.arc(px,py,2,0,Math.PI*2);
        ctx.fillStyle=hexToRgba(nodes[p.from].color,alpha*0.9); ctx.fill();
        for (let tr=1;tr<=3;tr++) {
          const tt=Math.max(0,p.t-tr*0.03);
          ctx.beginPath(); ctx.arc(x1+(x2-x1)*tt,y1+(y2-y1)*tt,1.2-tr*0.2,0,Math.PI*2);
          ctx.fillStyle=hexToRgba(nodes[p.from].color,alpha*0.25/tr); ctx.fill();
        }
      }
      [{color:'#06b6d4',s:-Math.PI/2-0.3,e:-Math.PI/2-0.3+Math.PI*0.85},
       {color:'#a855f7',s:Math.PI/2-0.25,e:Math.PI/2-0.25+Math.PI*0.75}
      ].forEach(g => {
        ctx.beginPath(); ctx.arc(CX,CY,327,g.s,g.e);
        ctx.strokeStyle=hexToRgba(g.color,0.12); ctx.lineWidth=1;
        ctx.setLineDash([3,6]); ctx.stroke(); ctx.setLineDash([]);
      });
      drawCenter(); nodes.forEach(drawNode);
      rafRef.current=requestAnimationFrame(loop);
    }
    function onMouseMove(e) {
      const rect=canvas.getBoundingClientRect();
      const s=scaleRef.current;
      const mx=(e.clientX-rect.left)/s, my=(e.clientY-rect.top)/s;
      let hov=null;
      for (const n of nodesRef.current) { const dx=n.x-mx,dy=n.y-my; if(Math.sqrt(dx*dx+dy*dy)<n.r+8){hov=n;break;} }
      hoveredRef.current=hov; setHoveredModule(hov); canvas.style.cursor=hov?'pointer':'default';
      if (hov) {
        let tx=e.clientX+15, ty=e.clientY-60;
        if(tx+280>window.innerWidth) tx=e.clientX-295;
        if(ty+150>window.innerHeight) ty=e.clientY-120;
        if(ty<10) ty=10;
        setTooltipPos({x:tx,y:ty});
      }
    }
    function onMouseLeave() { hoveredRef.current=null; setHoveredModule(null); canvas.style.cursor='default'; }
    canvas.addEventListener('mousemove',onMouseMove);
    canvas.addEventListener('mouseleave',onMouseLeave);
    const observer=new IntersectionObserver(([entry]) => {
      if(entry.isIntersecting&&!runningRef.current){runningRef.current=true;loop();}
      else if(!entry.isIntersecting){runningRef.current=false;if(rafRef.current)cancelAnimationFrame(rafRef.current);}
    },{threshold:0.1});
    observer.observe(canvas); loop();
    return () => {
      runningRef.current=false; if(rafRef.current)cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      canvas.removeEventListener('mousemove',onMouseMove);
      canvas.removeEventListener('mouseleave',onMouseLeave);
    };
  }, []);

  const details = hoveredModule ? MODULE_DETAILS[hoveredModule.label] : null;
  const sharedProps = { hoveredModule, nodesRef, hoveredRef, setHoveredModule };
  const HovIc = hoveredModule ? ICON_MAP[hoveredModule.label]?.Ic : null;

  return (
    <div style={{ background:'#080d1a', padding:'36px 20px', position:'relative', overflow:'hidden', minHeight:'100vh', boxSizing:'border-box' }}>
      <style>{`
        @keyframes mwBlink{0%,100%{opacity:1}50%{opacity:0.2}}
        @keyframes tooltipFade{from{opacity:0;transform:translateX(-5px)}to{opacity:1;transform:translateX(0)}}
        *{box-sizing:border-box;}
      `}</style>

      <div style={{position:'absolute',top:-200,right:-100,width:600,height:600,borderRadius:'50%',background:'radial-gradient(circle,rgba(59,130,246,0.06) 0%,transparent 65%)',pointerEvents:'none'}}/>
      <div style={{position:'absolute',bottom:100,left:-100,width:400,height:400,borderRadius:'50%',background:'radial-gradient(circle,rgba(168,85,247,0.05) 0%,transparent 65%)',pointerEvents:'none'}}/>

      {/* Legend */}
      <div style={{display:'flex',justifyContent:'center',gap:28,marginBottom:24,position:'relative',zIndex:2,flexWrap:'wrap'}}>
        {GROUPS.map(g=>(
          <div key={g.name} style={{display:'flex',alignItems:'center',gap:7}}>
            <div style={{width:8,height:8,borderRadius:'50%',background:g.color,boxShadow:`0 0 6px ${g.color}`}}/>
            <span style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:'#64748b',textTransform:'uppercase',letterSpacing:'1.5px'}}>{g.name}</span>
          </div>
        ))}
      </div>

      {/* 3-column layout */}
      <div ref={wrapRef} style={{ display:'flex', flexWrap:'wrap', gap:16, alignItems:'center', justifyContent:'center', maxWidth:1700, margin:'0 auto' }}>

        {/* LEFT */}
        <div style={{ flex:'1 1 320px', minWidth:0, maxWidth:560 }}>
          <SectionLabel label="Core Modules" color="#3b82f6" align="left" />
          <CardGrid modules={LEFT_MODULES} sharedProps={sharedProps} />
        </div>

        {/* CENTER canvas */}
        <div style={{ flexShrink:0, position:'relative', width:canvasSize, height:canvasSize }}>
          <canvas ref={canvasRef} width={W} height={H} style={{ width:canvasSize, height:canvasSize, display:'block', borderRadius:16 }}/>
          <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',textAlign:'center',pointerEvents:'none'}}>
            <div style={{fontFamily:"'DM Sans',system-ui,sans-serif",fontSize:Math.max(12,canvasSize*0.024),fontWeight:700,color:'#f1f5f9',letterSpacing:'-0.5px'}}>
              aero<span style={{color:'#3b82f6'}}>Trick</span>
            </div>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:Math.max(8,canvasSize*0.014),color:'#3b82f6',letterSpacing:'2px',textTransform:'uppercase',marginTop:3}}>
              18 modules
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div style={{ flex:'1 1 320px', minWidth:0, maxWidth:560 }}>
          <SectionLabel label="Airport Operations" color="#06b6d4" align="right" />
          <CardGrid modules={RIGHT_MODULES.slice(0,4)} sharedProps={sharedProps} />
          <div style={{marginTop:18}}>
            <SectionLabel label="Analytics" color="#a855f7" align="right" />
            <CardGrid modules={RIGHT_MODULES.slice(4)} sharedProps={sharedProps} />
          </div>
        </div>
      </div>

      {/* Status */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginTop:28,flexWrap:'wrap'}}>
        <div style={{width:6,height:6,background:'#22c55e',borderRadius:'50%',animation:'mwBlink 1.5s infinite'}}/>
        <span style={{fontFamily:"'DM Mono',monospace",fontSize:12,color:'#64748b'}}>
          18 modules · All systems operational · aeroTrick v2.0
        </span>
      </div>

      {/* Tooltip */}
      {hoveredModule && details && (
        <div style={{
          position:'fixed', left:tooltipPos.x, top:tooltipPos.y, width:270,
          background:'#0f172a', borderRadius:12,
          border:`1px solid ${hexToRgba(hoveredModule.color,0.4)}`,
          boxShadow:'0 4px 24px rgba(0,0,0,0.35)', padding:'14px 16px',
          zIndex:1000, animation:'tooltipFade 0.15s ease', pointerEvents:'none',
          backdropFilter:'blur(8px)',
        }}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:9}}>
            <div style={{
              width:36,height:36,borderRadius:'50%',
              background:hexToRgba(hoveredModule.color,0.18),
              border:`1px solid ${hexToRgba(hoveredModule.color,0.45)}`,
              display:'flex',alignItems:'center',justifyContent:'center',
            }}>
              {HovIc && <HovIc size={18} color={hoveredModule.color} strokeWidth={1.8}/>}
            </div>
            <div>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <span style={{fontFamily:"'DM Sans',system-ui",fontSize:13,fontWeight:700,color:'#f1f5f9'}}>{hoveredModule.label}</span>
                <span style={{background:hexToRgba(hoveredModule.color,0.2),color:hoveredModule.color,fontSize:9,fontWeight:600,padding:'2px 7px',borderRadius:10}}>{details.tag}</span>
              </div>
              <span style={{fontSize:9,color:GROUPS[hoveredModule.group]?.color,fontFamily:"'DM Mono',monospace"}}>{GROUPS[hoveredModule.group]?.name}</span>
            </div>
          </div>
          <p style={{fontFamily:"'DM Sans',system-ui",fontSize:12,lineHeight:1.5,color:'#94a3b8',margin:0,paddingTop:8,borderTop:'1px solid rgba(100,116,139,0.2)'}}>
            {details.desc}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── SVG Path2D data for canvas rendering (Lucide icon paths, 24×24 viewBox) ──
const CANVAS_PATHS = {
  // PlaneTakeoff
  'Movement & Billing': [
    'M2 22h20',
    'M6.36 17.4 4 17l-2-4 1.1-.55a2 2 0 0 1 1.8 0l.17.1a2 2 0 0 0 1.8 0L8 12 5 6l.9-.45a2 2 0 0 1 2.09.2l4.02 3a2 2 0 0 0 2.1.2l4.19-2.06a2.41 2.41 0 0 1 1.73-.17L21 7a1.4 1.4 0 0 1 .87 1.99l-.38.76c-.23.46-.6.84-1.07 1.08L7.58 17.2a2 2 0 0 1-1.22.18z',
  ],
  // Fuel / droplet + flame
  'Fuel Management': [
    'M3 22a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1h2v-2a7 7 0 1 1 14 0v9a1 1 0 0 1-1 1z',
    'M11 10.5a.5.5 0 0 0 1 0V3a.5.5 0 0 0-1 0z',
    'M10 7h4',
  ],
  // CalendarDays (Staff Scheduling) - version corrigée
  'Staff Scheduling': [
    'M8 2v4 M16 2v4',
    'M3 10h18',
    'M4 4h16a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
    'M8 14h.01 M12 14h.01 M16 14h.01',
    'M8 18h.01 M12 18h.01 M16 18h.01',
  ],
  // Radar
  'Live Traffic Map': [
    'M19.07 4.93A10 10 0 0 0 6.99 3.34',
    'M4 6 A10 10 0 1 0 20 12',
    'M12 12 19.07 4.93',
    'M10 10a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
  ],
  // MessageSquare
  'Client Chat': [
    'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  ],
  // CalendarCheck (Appointments) - version corrigée
  'Appointments': [
    'M8 2v4 M16 2v4',
    'M3 10h18',
    'M4 4h16a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
    'M9 16l2 2 4-4',
  ],
  // Truck
  'Fleet Management': [
    'M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3',
    'M12 12h10v5H12z',
    'M7 17a2 2 0 1 0 0-4a2 2 0 0 0 0 4',
    'M17 17a2 2 0 1 0 0-4a2 2 0 0 0 0 4',
    'M9 17h6',
  ],
  // Receipt
  'Invoicing & Hangars': [
    'M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z',
    'M14 8H8 M16 12H8 M13 16H8',
  ],
  // ClipboardList (Inspections) - version corrigée
  'Inspections': [
    'M8 2h8a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z',
    'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2',
    'M12 11h4',
    'M12 16h4',
    'M8 11h.01 M8 16h.01',
  ],
  // MapPin
  'Airport Zones': [
    'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z',
    'M10 10a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
  ],
  // RefreshCw
  'Recurring Tasks': [
    'M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8',
    'M21 3v5h-5',
    'M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16',
    'M8 16H3v5',
  ],
  // GraduationCap
  'Certifications': [
    'M22 10v6 M2 10l10-5 10 5-10 5z',
    'M6 12v5c3 3 9 3 12 0v-5',
  ],
  // ShieldAlert
  'Deletion Audit': [
    'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    'M12 8v4 M12 16h.01',
  ],
  // TrendingUp
  'Fuel Analytics': [
    'M22 7 13.5 15.5 8.5 10.5 2 17',
    'M16 7h6v6',
  ],
  // Wrench
  'Maint Analytics': [
    'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
  ],
  // DollarSign
  'Financial Analytics': [
    'M12 2v20',
    'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  ],
  // BarChart3
  'Flight Analytics': [
    'M3 3v18h18',
    'M18 17V9 M13 17V5 M8 17v-3',
  ],
  // Warehouse
  'Hangar Analytics': [
    'M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35z',
    'M6 18h12 M6 14h12',
    'M8 18v4h8v-4z',
  ],
};

// Patch: handle SVG element shorthand in paths (rect, circle, polygon)
// We override the canvas drawNode to handle these specially
// The paths above use a mixed format: some are 'd' strings, some are element descriptors.
// Let's normalize at module load time.
(function normalizeCanvasPaths() {
  for (const key of Object.keys(CANVAS_PATHS)) {
    CANVAS_PATHS[key] = CANVAS_PATHS[key].map(d => {
      if (d.startsWith('rect ')) {
        const m = d.match(/x=([\d.]+) y=([\d.]+) width=([\d.]+) height=([\d.]+)/);
        if (m) {
          const [,x,y,w,h] = m.map(Number);
          const rx = (d.match(/rx=([\d.]+)/) || [])[1] || 0;
          return `M${+x+rx},${y} H${+x+ +w-rx} Q${+x+ +w},${y} ${+x+ +w},${+y+rx} V${+y+ +h-rx} Q${+x+ +w},${+y+ +h} ${+x+ +w-rx},${+y+ +h} H${+x+rx} Q${x},${+y+ +h} ${x},${+y+ +h-rx} V${+y+rx} Q${x},${y} ${+x+rx},${y} Z`;
        }
      }
      if (d.startsWith('circle ')) {
        const cx = +(d.match(/cx=([\d.]+)/)||[])[1];
        const cy = +(d.match(/cy=([\d.]+)/)||[])[1];
        const r  = +(d.match(/r=([\d.]+)/)||[])[1];
        return `M${cx-r},${cy} a${r},${r} 0 1,0 ${r*2},0 a${r},${r} 0 1,0 -${r*2},0`;
      }
      if (d.startsWith('polygon ')) {
        const pts = (d.match(/points=([^\s]+)/)||[])[1];
        if (pts) {
          const coords = pts.split(' ');
          return 'M' + coords.join('L') + 'Z';
        }
      }
      return d;
    });
  }
})();